import React, { useState, useEffect, useCallback } from "react";
import ComplaintService, {
  PatientComplaint,
} from "../../../../services/Complaintservice";
import ComplaintsHistory from "./Complaintshistory";
import ComplaintsModal from "./Complaintsmodal";

interface PatientComplaintsTabProps {
  patientId: number;
  patientName?: string;
}

const PatientComplaintsTab: React.FC<PatientComplaintsTabProps> = ({
  patientId,
  patientName = "Patient",
}) => {
  const [complaints, setComplaints] = useState<PatientComplaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] =
    useState<PatientComplaint | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadComplaints = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    setStatsLoading(true);
    setError(null);
    try {
      const data = await ComplaintService.getPatientComplaints(patientId, 50);
      setComplaints(data);
    } catch (err: any) {
      setError(err.message || "Failed to load complaints");
      console.error("Error loading complaints:", err);
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadComplaints();
  }, [loadComplaints]);

  const handleAddNew = () => {
    setSelectedComplaint(null);
    setShowModal(true);
  };

  const handleEdit = (complaint: PatientComplaint) => {
    setSelectedComplaint(complaint);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedComplaint(null);
  };

  const handleSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
    loadComplaints();
  };

  const handleDelete = (complaintId: number) => {
    setComplaints((prev) => prev.filter((c) => c.id !== complaintId));
  };

  const calculateStats = () => {
    const lastComplaintDate =
      complaints.length > 0
        ? complaints.reduce(
            (latest, complaint) =>
              !latest || new Date(complaint.recorded_at) > new Date(latest)
                ? complaint.recorded_at
                : latest,
            null as string | null
          )
        : null;

    return {
      lastComplaintDate: lastComplaintDate
        ? ComplaintService.formatDate(lastComplaintDate)
        : "--",
    };
  };

  return (
    <>
      <div className="complaints-container">
        {/* Header with Action Buttons */}
        <div className="complaints-header">
          <div className="header-info">
            <h3>Patient Complaints</h3>
            <p>
              Record and manage patient complaints history for {patientName}
            </p>
          </div>

          <div className="header-actions">
            <button
              className="primary-button"
              onClick={handleAddNew}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#2563eb")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#3b82f6")
              }
            >
              <span className="button-icon">+</span>
              <span className="button-text">Record Complaint</span>
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="error-alert">
            <span>{error}</span>
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {/* Complaints History - Inline */}
        <ComplaintsHistory
          complaints={complaints}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />

        {/* Complaint Statistics */}
        <div className="complaint-statistics">
          <h4 className="stats-title">Complaint Activity Summary</h4>

          {statsLoading ? (
            <div className="stats-loading">
              <div className="spinner" />
            </div>
          ) : (
            <div className="stats-grid">
              {(() => {
                const stats = calculateStats();
                return (
                  <>
                    <div className="stat-item">
                      <div className="stat-value neutral">
                        {stats.lastComplaintDate}
                      </div>
                      <div className="stat-label">Last Complaint Date</div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Complaints Modal */}
      <ComplaintsModal
        show={showModal}
        onHide={handleModalClose}
        patientId={patientId}
        patientName={patientName}
        complaint={selectedComplaint}
        onSuccess={handleSuccess}
      />

      <style>
        {`
        .complaints-container {
          padding: 16px;
          height: 100%;
          overflow: auto;
        }

        .complaints-header {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
          padding: 20px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .header-info h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #1f2937;
        }

        .header-info p {
          font-size: 13px;
          color: #6b7280;
          margin: 0;
        }

        .header-actions {
          display: flex;
          justify-content: center;
        }

        .primary-button {
          padding: 12px 20px;
          border-radius: 8px;
          border: none;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          justify-content: center;
        }

        .button-icon {
          font-size: 16px;
          font-weight: bold;
        }

        .button-text {
          white-space: nowrap;
        }

        .error-alert {
          background: #fee2e2;
          border: 1px solid #ef4444;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #991b1b;
          font-size: 14px;
        }

        .error-alert button {
          background: none;
          border: none;
          font-size: 20px;
          color: #991b1b;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .complaint-statistics {
          background: #ffffff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .stats-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 16px;
          color: #1f2937;
        }

        .stats-loading {
          text-align: center;
          padding: 20px;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #e2e8f0;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(1, 1fr);
          gap: 16px;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .stat-value.neutral {
          color: #6b7280;
        }

        .stat-label {
          font-size: 12px;
          color: #6b7280;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Tablet styles */
        @media (min-width: 640px) {
          .complaints-container {
            padding: 24px;
          }

          .complaints-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .header-info h3 {
            font-size: 20px;
          }

          .header-info p {
            font-size: 14px;
          }

          .header-actions {
            justify-content: flex-end;
          }

          .primary-button {
            width: auto;
            min-width: 160px;
          }

          .stats-grid {
            grid-template-columns: repeat(1, 1fr);
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(1, 1fr);
          }
        }

        .primary-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}
      </style>
    </>
  );
};

export default PatientComplaintsTab;
